import { collection, serverTimestamp } from 'firebase/firestore'
import {
  convertDocumentSnapshotToModel,
  modelListStream,
} from '../../firestore-mobx/stream'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { SectionProduct } from '../../models/SectionProduct'
import type { FirestoreSectionProduct } from './schema'
import { schema, writeSchema } from './schema'
import { addDocWithError, getDocsWithError } from '../../firestore-mobx/fetch'
import type {
  FieldValue,
  CollectionReference,
  PartialWithFieldValue,
  Firestore,
  FirestoreDataConverter,
  QueryDocumentSnapshot,
} from 'firebase/firestore'

export * from './schema'

const converter: FirestoreDataConverter<FirestoreSectionProduct> = {
  toFirestore: (data: PartialWithFieldValue<FirestoreSectionProduct>) => {
    writeSchema.partial().parse(data)

    return data
  },
  fromFirestore: (snapshot: QueryDocumentSnapshot) => {
    const data = snapshot.data({ serverTimestamps: 'estimate' })
    return schema.parse(data)
  },
}

export const getColRef = (
  firestore: Firestore,
  sectionId: string
): CollectionReference<FirestoreSectionProduct> => {
  return collection(
    firestore,
    'section',
    sectionId,
    'section_product'
  ).withConverter(converter)
}

export const getSectionProductsBySectionId = (
  repository: FirebaseRepository,
  { sectionId }: { sectionId: string }
) => {
  if (!sectionId) {
    throw new Error(
      'Nonempty sectionId is required for getSectionProductsBySectionId'
    )
  }

  const ref = getColRef(repository.firestore, sectionId)

  return modelListStream(repository, ref, SectionProduct)
}

export const createSectionProduct = (
  repository: FirebaseRepository,
  {
    productId,
    sectionId,
    customProduct,
    currentUserId,
  }: {
    productId: string
    sectionId: string
    customProduct: boolean
    currentUserId: string
  }
) => {
  const payload: Omit<FirestoreSectionProduct, 'updatedAt'> & {
    updatedAt: FieldValue
    creatorUserId: string
    userId: string
  } = {
    productId,
    sectionId,
    customProduct,
    userId: currentUserId,
    creatorUserId: repository.uid,
    updatedAt: serverTimestamp(),
  }

  return addDocWithError(
    getColRef(repository.firestore, sectionId),
    payload,
    'CreateSectionProductError'
  )
}

export const fetchMostRecentSectionProduct = async (
  repository: FirebaseRepository,
  { sectionId }: { sectionId: string }
) => {
  const ref = getColRef(repository.firestore, sectionId)

  const snapshot = await getDocsWithError(
    ref,
    'FetchMostRecentSectionProductError'
  )

  if (snapshot.empty) {
    return null
  }

  const docs = snapshot.docs.sort((a, b) => {
    const aTime = a.data().updatedAt?.getTime() || 0
    const bTime = b.data().updatedAt?.getTime() || 0
    return bTime - aTime
  })

  return convertDocumentSnapshotToModel(repository, docs[0], SectionProduct)
}
